import { GInfoPanelPopover, GSkeletonDisplayText } from '~/presentation/common';
import { EScrollType, useHeatmapSettingContext } from '@gem-sdk/heatmap-react';
import { useHeatmapLoadingContext } from '~/modules/heatmap/hooks';
import { HEATMAP_SCROLL_CONFIG } from '~/modules/heatmap/configs';
import { EHeatmapLoadingKey } from '~/modules/heatmap/types';
import { BlockStack, Text, Box } from '@shopify/polaris';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SPACING_INSET } from '../Sidebar';

const SelectScrollTypeSkeleton: React.FC = () => (
  <Box paddingBlock="300" paddingBlockEnd="200">
    <div style={{ width: '133px' }}>
      <GSkeletonDisplayText height="12px" />
    </div>
  </Box>
);

export const SelectScrollType: React.FC = () => {
  const isLoading = useHeatmapLoadingContext((s) =>
    s.getLoadingByKey(EHeatmapLoadingKey.HEATMAP_SCROLL),
  );
  const scrollType = useHeatmapSettingContext((state) => state.scrollType);
  const setScrollType = useHeatmapSettingContext((state) => state.setScrollType);

  const { t } = useTranslation();

  const handleSelectChange = useCallback(
    (value: string) => setScrollType(value as EScrollType),
    [setScrollType],
  );

  const items = useMemo(
    () => [
      {
        id: EScrollType.Depth,
        label: t('Scroll depth'),
        data: {
          title: t('Scroll depth'),
          description: t('Find out how far users scroll and what they miss'),
        },
      },
      {
        id: EScrollType.Attention,
        label: t('Attention scroll'),
        data: {
          title: t('Attention scroll'),
          description: t('Find out how long users spend on each part of the page'),
        },
      },
      {
        id: EScrollType.Revenue,
        label: t('Revenue scroll'),
        data: {
          title: t('Revenue scroll'),
          description: t('Find out how much revenue each part of the page generates'),
        },
      },
    ],
    [t],
  );

  useEffect(() => {
    if (scrollType) return;

    setScrollType(HEATMAP_SCROLL_CONFIG.SCROLL_TYPE);
  }, [scrollType, setScrollType]);

  return (
    <BlockStack gap={SPACING_INSET}>
      <Text as="span" variant="bodyMd" fontWeight="medium">
        {t('Scroll type')}
      </Text>

      {isLoading ? (
        <SelectScrollTypeSkeleton />
      ) : (
        <GInfoPanelPopover
          items={items}
          selectedId={scrollType}
          onSelect={handleSelectChange}
          fullWidth
          minWidth="458px"
          minHeight="108px"
          columns="236px 1fr"
        />
      )}
    </BlockStack>
  );
};
